import { useQuery } from '@tanstack/react-query'
import { apiGet } from './client'
import { useSupabaseToken } from '../hooks/useSupabaseToken'
import { decodeFinishQuota, type FinishQuota } from './finishDeck'

// GET /pro/status payload, verbatim. Server field names are kept in snake_case
// (the house convention for REST response types, see src/api/types.ts) so the
// wire contract is greppable from the UI. The SSE quota block in
// lib/proChatStream.ts is camelCased instead because it goes through a parser.
export interface ProQuota {
  used_month: number
  cap_month: number
  // null cap_day for free users: they only have a monthly cap.
  used_day: number
  cap_day: number | null
  month_resets_at: string // YYYY-MM-DD
  day_resets_at: string   // YYYY-MM-DD
}

export type ProEntitlementStatus = 'active' | 'grace' | 'expired' | 'revoked' | 'none'

export interface ProStatus {
  // The ONLY free/pro discriminator. Never infer entitlement from `status`.
  is_pro: boolean
  status: ProEntitlementStatus
  expires_at: number | null // epoch seconds
  quota: ProQuota
  // The PRO tier's monthly message cap, served to every caller (a free
  // user's quota.cap_month is the free cap) so the paywall quotes the real
  // number. Optional only for a server that predates the field; the paywall
  // falls back to PRO_MONTHLY_MESSAGES then.
  pro_cap_month?: number
  /** Watchtower Set Check. Absent on an API older than 2026-09-24. */
  set_check?: { enabled: boolean; cap_day: number; used_day: number; resets_at: string }
  /**
   * Finish my deck. Present ONLY while the API's FINISH_MY_DECK flag is on:
   * its presence is the signed-in availability gate (absent: every Finish
   * entry point hides). Read it through decodeFinishQuota, never raw.
   */
  finish?: FinishQuota
}

// There is no server-sent `remaining`: callers compute cap - used themselves.

/**
 * Pure read of the caller's Pro entitlement and chat quota. Never burns a
 * message, so it is safe to call on focus, after a purchase, or on any 429.
 */
export function getProStatus(bearerToken: string): Promise<ProStatus> {
  return apiGet<ProStatus>('/pro/status', { bearerToken })
}

/**
 * Does this reader get a purchase affordance AT ALL?
 *
 * The ONE rule for every surface that sells Pro - the account card, the
 * account menu, Home's band - so a tri-state entitlement cannot read as
 * "free" on one surface and "unknown" on another. `undefined` is "no
 * successful read yet", which react-query also reports while a refetch is in
 * flight and after an identity change; reading it as free is how somebody
 * already paying gets shown a purchase button.
 */
export function offersPurchase(status: ProStatus | undefined): boolean {
  return !!status && !status.is_pro
}

/**
 * Whether a status in hand offers Finish my deck: its `finish` block is
 * present (and well formed). No status (signed out, the first read in
 * flight) is NOT an offer: the paywall names Finish only on a positive read.
 * The paywall's Finish copy reads this (ProUpgradeModal `finishOffered`).
 */
export function finishOfferedByStatus(status: Pick<ProStatus, 'finish'> | null | undefined): boolean {
  return !!status && decodeFinishQuota(status.finish) !== null
}

export function proStatusQueryKey(userId: string | null) {
  // eslint-disable-next-line i18next/no-literal-string -- query key
  return ['pro-status', userId] as const
}

/**
 * Entitlement + quota for the signed-in user.
 *
 * Gated on BOTH token and userId: the query is keyed by userId, and a token
 * without a resolved user id would cache under a null key.
 *
 * Consumers push the chat `done` frame quota in with setQueryData, and
 * invalidate this key on an error frame, any 429, a post-purchase activation,
 * or a stream that delivered a message but closed without a `done` frame.
 */
export function useProStatus() {
  const { token, userId } = useSupabaseToken()

  return useQuery<ProStatus>({
    queryKey: proStatusQueryKey(userId),
    queryFn: () => getProStatus(token!),
    enabled: !!token && !!userId,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  })
}

// Post-purchase activation window. The server's entitlement cache TTL is 60s
// and the RevenueCat webhook can lag, so the client polls rather than assuming
// the first read after a purchase is authoritative.
export const PRO_STATUS_POLL_INTERVAL_MS = 3000
export const PRO_STATUS_POLL_TIMEOUT_MS = 90_000

export interface PollProStatusOptions {
  intervalMs?: number
  timeoutMs?: number
  signal?: AbortSignal
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    const onAbort = () => {
      clearTimeout(timer)
      resolve()
    }
    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

/**
 * Polls /pro/status until the entitlement turns on, for use immediately after
 * a purchase.
 *
 * Resolves with the status the moment `is_pro` is true, or null when the
 * timeout elapses (or the caller aborts). NEVER rejects and never stops on a
 * single failed read: a 5xx or a network blip during activation is expected,
 * and a completed charge must never be presented to the user as a failure. A
 * null result means "still activating", not "payment failed".
 */
export async function pollProStatusUntilPro(
  bearerToken: string,
  options: PollProStatusOptions = {},
): Promise<ProStatus | null> {
  const intervalMs = options.intervalMs ?? PRO_STATUS_POLL_INTERVAL_MS
  const timeoutMs = options.timeoutMs ?? PRO_STATUS_POLL_TIMEOUT_MS
  const signal = options.signal
  const startedAt = Date.now()

  for (;;) {
    if (signal?.aborted) return null
    try {
      const status = await getProStatus(bearerToken)
      if (status?.is_pro) return status
    } catch {
      // Transient failure during activation: keep polling until the deadline.
    }
    if (signal?.aborted) return null
    if (Date.now() - startedAt + intervalMs > timeoutMs) return null
    await sleep(intervalMs, signal)
  }
}
